import { StreamTypes } from 'store/player/actions.types';

import { VideoItem } from 'services/data-manager/types';

type Params = {
    videoItem: VideoItem | undefined;
    playbackType: StreamTypes | undefined;
    timeshiftAllowed: boolean;
    timeshiftModeOn: boolean;
    startoverAllowed: boolean;
};

export enum PlayerControlsAccessLevel {
    // Totally unavalable. Will hide player controls button.
    DISABLED = 'DISABLED',
    // Visible, but greyed out. onPress will produce appropriate notification about the restriction.
    RESTRICTED = 'RESTRICTED',
    // Fully enabled and visible. onPress will produce appropriate player action.
    ENABLED = 'ENABLED',
}

type ReturnType = {
    rewindAccessLevel: PlayerControlsAccessLevel;
    pauseAccessLevel: PlayerControlsAccessLevel;
    fastforwardAccessLevel: PlayerControlsAccessLevel;
};

const DEFAULT_TRICK_MODE_CONTROL: ReturnType = {
    rewindAccessLevel: PlayerControlsAccessLevel.DISABLED,
    pauseAccessLevel: PlayerControlsAccessLevel.DISABLED,
    fastforwardAccessLevel: PlayerControlsAccessLevel.DISABLED,
};

const VOD_TRICK_MODE_CONTROL: ReturnType = {
    rewindAccessLevel: PlayerControlsAccessLevel.ENABLED,
    pauseAccessLevel: PlayerControlsAccessLevel.ENABLED,
    fastforwardAccessLevel: PlayerControlsAccessLevel.ENABLED,
};

function useTrickModeControl({
    videoItem,
    playbackType,
    timeshiftAllowed,
    timeshiftModeOn,
    startoverAllowed,
}: Params): ReturnType {
    const isLive = playbackType === StreamTypes.live;
    const isStartover = playbackType === StreamTypes.startover;
    const isCatchup = playbackType === StreamTypes.catchup;
    const isNpvr = playbackType === StreamTypes.npvr;
    const isVod = playbackType === StreamTypes.vod;

    if (!videoItem) {
        return DEFAULT_TRICK_MODE_CONTROL;
    }

    const {
        trickModesControl: {
            live: {
                pauseAllowed: livePauseAllowed,
                rewindAllowed: liveRewindAllowed,
                fastforwardAllowed: liveFastforwardAllowed,
            },
            vod: {
                pauseAllowed: vodPauseAllowed,
                rewindAllowed: vodRewindAllowed,
                fastforwardAllowed: vodFastforwardAllowed,
            },
            recording: {
                pauseAllowed: recordingPauseAllowed,
                rewindAllowed: recordingRewindAllowed,
                fastforwardAllowed: recordingFastforwardAllowed,
            },
        },
    } = videoItem;

    // For now, isStartover means that the live program was switched to TS or SO mode, so
    // all the cases are dependent on "live" playbackType
    if (isLive || isStartover) {
        const liveAccessLevels = { ...DEFAULT_TRICK_MODE_CONTROL };

        if (timeshiftModeOn || startoverAllowed) {
            liveAccessLevels.rewindAccessLevel = liveRewindAllowed
                ? PlayerControlsAccessLevel.ENABLED
                : PlayerControlsAccessLevel.RESTRICTED;
            liveAccessLevels.fastforwardAccessLevel = liveFastforwardAllowed
                ? PlayerControlsAccessLevel.ENABLED
                : PlayerControlsAccessLevel.RESTRICTED;
        }

        if (timeshiftAllowed || startoverAllowed) {
            liveAccessLevels.pauseAccessLevel = livePauseAllowed
                ? PlayerControlsAccessLevel.ENABLED
                : PlayerControlsAccessLevel.RESTRICTED;
        }

        return liveAccessLevels;
    }

    if (isCatchup) {
        return {
            ...DEFAULT_TRICK_MODE_CONTROL,
            rewindAccessLevel: vodRewindAllowed
                ? PlayerControlsAccessLevel.ENABLED
                : PlayerControlsAccessLevel.RESTRICTED,
            pauseAccessLevel: vodPauseAllowed
                ? PlayerControlsAccessLevel.ENABLED
                : PlayerControlsAccessLevel.RESTRICTED,
            fastforwardAccessLevel: vodFastforwardAllowed
                ? PlayerControlsAccessLevel.ENABLED
                : PlayerControlsAccessLevel.RESTRICTED,
        };
    }

    if (isNpvr) {
        return {
            ...DEFAULT_TRICK_MODE_CONTROL,
            rewindAccessLevel: recordingRewindAllowed
                ? PlayerControlsAccessLevel.ENABLED
                : PlayerControlsAccessLevel.RESTRICTED,
            pauseAccessLevel: recordingPauseAllowed
                ? PlayerControlsAccessLevel.ENABLED
                : PlayerControlsAccessLevel.RESTRICTED,
            fastforwardAccessLevel: recordingFastforwardAllowed
                ? PlayerControlsAccessLevel.ENABLED
                : PlayerControlsAccessLevel.RESTRICTED,
        };
    }

    if (isVod) {
        return VOD_TRICK_MODE_CONTROL;
    }

    return DEFAULT_TRICK_MODE_CONTROL;
}

export default useTrickModeControl;

export function accessIsDisabled(accessLevel: PlayerControlsAccessLevel) {
    return accessLevel === PlayerControlsAccessLevel.DISABLED;
}

export function accessIsRestricted(accessLevel: PlayerControlsAccessLevel) {
    return accessLevel === PlayerControlsAccessLevel.RESTRICTED;
}

export function accessIsEnabled(accessLevel: PlayerControlsAccessLevel) {
    return accessLevel === PlayerControlsAccessLevel.ENABLED;
}
